import React from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { GraduationCap, ArrowRight, Baby, BookOpen, Briefcase, Award, AlertCircle, Euro, Clock } from 'lucide-react';

const EducationPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white" data-testid="education-page">
      <Navbar />

      <section className="py-16 md:py-20 px-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100/40 rounded-full filter blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="container mx-auto max-w-4xl relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <GraduationCap className="w-4 h-4" />
            Integration Module
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-babyblue-800 mb-4">
            Education &amp; Training
          </h1>
          <p className="text-xl text-babyblue-600 leading-relaxed max-w-3xl mx-auto">
            From Kindergarten to Studium — every step in the German education system.
          </p>
        </div>
      </section>

      {/* Federalism note */}
      <section className="py-8 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-amber-50 border-l-4 border-amber-400 p-5 rounded-r-xl flex items-start gap-3" data-testid="federal-note">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-amber-900">
              <strong>Important:</strong> Education in Germany is run by each <em>Bundesland</em> (federal state). The structure below is the national framework — exact rules, ages and grades can vary state by state.
            </p>
          </div>
        </div>
      </section>

      {/* Kindergarten */}
      <section className="py-8 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6" data-testid="kindergarten-card">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-yellow-100 rounded-xl flex-shrink-0"><Baby className="w-7 h-7 text-yellow-700" /></div>
              <div>
                <h2 className="font-heading text-xl font-bold text-babyblue-800 mb-1">Kindergarten &amp; KiTa</h2>
                <p className="text-babyblue-500 italic text-sm mb-3">Ages 0–6</p>
                <p className="text-babyblue-700">
                  Kindergarten is <strong>not mandatory</strong>, but every child has the legal right to a place. <strong>KiTa</strong> (Kindertagesstätte) cares for babies and toddlers. Many municipalities subsidise the costs.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Grundschule */}
      <section className="py-8 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6" data-testid="grundschule-card">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-green-100 rounded-xl flex-shrink-0"><BookOpen className="w-7 h-7 text-green-700" /></div>
              <div>
                <h2 className="font-heading text-xl font-bold text-babyblue-800 mb-1">Grundschule</h2>
                <p className="text-babyblue-500 italic text-sm mb-3">Primary school — ages 6 to 10</p>
                <p className="text-babyblue-700 mb-3">
                  Kids learn to read, <strong>write</strong>, and live together. Most primary schools include PE class and lots of applied learning — forest trips, hands-on workshops, and visits from guest experts.
                </p>
                <p className="text-babyblue-700">
                  After 4th grade, the family chooses a secondary school. You can pick freely, but the homeroom teacher gives a <em>Schullaufbahnempfehlung</em> (school-path recommendation).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Weiterführende Schule */}
      <section className="py-12 px-6 bg-gradient-to-b from-babyblue-50/40 to-white">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-2xl font-bold text-babyblue-800 mb-6 text-center">
            Weiterführende Schule (Secondary School)
          </h2>
          <div className="space-y-4">
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6" data-testid="gymnasium-card">
              <h3 className="font-bold text-babyblue-800 text-lg mb-1">Gymnasium</h3>
              <p className="text-babyblue-500 italic text-sm mb-2">Academic track</p>
              <p className="text-babyblue-700">
                The academically-focused secondary school. Strong on theory, languages and sciences. Students graduate with the <strong>Abitur</strong> — the qualification that opens university doors.
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6" data-testid="realschule-card">
              <h3 className="font-bold text-babyblue-800 text-lg mb-1">Realschule</h3>
              <p className="text-babyblue-500 italic text-sm mb-2">Theory + applied — through 10th grade</p>
              <p className="text-babyblue-700 mb-2">
                For motivated learners who like both theory and applied sciences. The subjects largely overlap with Gymnasium but go less deep; many schools add IT, technical and culinary tracks.
              </p>
              <p className="text-babyblue-700">
                After the <strong>Realschulabschluss</strong>, students choose between an <strong>Ausbildung</strong>, or continuing at a Berufsschule / Fachoberschule to earn the <em>Fachhochschulreife</em> or even the <em>Abitur</em>.
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6" data-testid="hauptschule-card">
              <h3 className="font-bold text-babyblue-800 text-lg mb-1">Hauptschule</h3>
              <p className="text-babyblue-500 italic text-sm mb-2">Hands-on through 9th grade</p>
              <p className="text-babyblue-700">
                Focuses on practical learning. After 9th grade, students can either go on to earn the Realschulabschluss or move straight into an Ausbildung in hands-on fields like mechanics, baking, hairdressing, or construction.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ausbildung */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-6 md:p-8">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-blue-100 rounded-xl flex-shrink-0"><Briefcase className="w-7 h-7 text-blue-700" /></div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-babyblue-800">Ausbildung</h2>
                <p className="text-babyblue-500 italic text-sm">Vocational training programme</p>
              </div>
            </div>
            <p className="text-babyblue-700 leading-relaxed mb-5">
              The Ausbildung combines classroom theory at a <strong>Berufsschule</strong> (vocational school) with applied experience inside an <strong>Ausbildungsbetrieb</strong> (host company). It usually lasts 2–3 years.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5" data-testid="duale-card">
                <h3 className="font-bold text-blue-900 mb-1">Duale Ausbildung</h3>
                <p className="text-sm text-blue-800">
                  <strong>Paid</strong> traineeship — you alternate between school and the workplace. Your employer pays you a monthly Ausbildungsvergütung from day one.
                </p>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5" data-testid="schulische-card">
                <h3 className="font-bold text-blue-900 mb-1">Schulische Ausbildung</h3>
                <p className="text-sm text-blue-800">
                  School-based programme with practical placements through the school. Usually <strong>unpaid</strong> — you may even pay tuition. Typical for physiotherapy, design and other creative professions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Studium */}
      <section className="py-12 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="bg-gradient-to-br from-blue-50 to-blue-50 border-2 border-blue-300 rounded-2xl p-6 md:p-8" data-testid="studium-card">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-blue-200 rounded-xl flex-shrink-0"><Award className="w-7 h-7 text-blue-800" /></div>
              <div>
                <h2 className="font-heading text-2xl font-bold text-blue-900">Studium</h2>
                <p className="text-blue-700 italic text-sm">University &amp; Fachhochschule</p>
              </div>
            </div>
            <p className="text-blue-900 leading-relaxed mb-4">
              You can study with either the Abitur or the Fachhochschulreife:
            </p>
            <ul className="space-y-2 text-blue-900 mb-4">
              <li><strong>Abitur</strong> → Eligible for both Universität and Fachhochschule (FH, University of Applied Sciences).</li>
              <li><strong>Fachhochschulreife</strong> → Eligible for Fachhochschule.</li>
            </ul>
            <p className="text-blue-900">
              After a Bachelor's, you can pursue a Master's and start your career. Always double-check entry requirements with your Bundesland — they vary.
            </p>
          </div>
        </div>
      </section>

      {/* Work types */}
      <section className="py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          <h2 className="font-heading text-2xl font-bold text-babyblue-800 mb-6 text-center">Work Models</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-5" data-testid="minijob-card">
              <div className="flex items-center gap-2 mb-2"><Euro className="w-5 h-5 text-emerald-600" /><h3 className="font-bold text-babyblue-800">Minijob</h3></div>
              <p className="text-sm text-babyblue-700">
                Up to <strong>€538/month</strong> (2025 cap, often quoted as ~€520). Great side income for clothes, coffee or cinema — tax-light and bureaucracy-light.
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-5" data-testid="vollzeit-card">
              <div className="flex items-center gap-2 mb-2"><Clock className="w-5 h-5 text-blue-600" /><h3 className="font-bold text-babyblue-800">Vollzeit</h3></div>
              <p className="text-sm text-babyblue-700">
                Full-time, typically <strong>35–40 hours/week</strong>. Full social-insurance contributions and full benefits.
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-md border border-babyblue-200 p-5" data-testid="teilzeit-card">
              <div className="flex items-center gap-2 mb-2"><Clock className="w-5 h-5 text-blue-600" /><h3 className="font-bold text-babyblue-800">Teilzeit</h3></div>
              <p className="text-sm text-babyblue-700">
                Part-time — anything <strong>under 35 hours/week</strong>. Pro-rated salary and benefits.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-12 px-6 bg-gradient-to-b from-babyblue-50 to-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="font-heading text-xl font-bold text-babyblue-800 mb-6">Continue Your Integration</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { icon: '💼', title: 'Career Paths', path: '/careers/business' },
              { icon: '🏛️', title: 'Opening a Business', path: '/integration/business-setup' },
              { icon: '🚗', title: "Driver's License", path: '/integration/fuhrerschein' },
            ].map((item, i) => (
              <Link key={i} to={item.path} className="bg-white rounded-xl border border-babyblue-200 p-4 flex items-center gap-3 hover:border-primary hover:shadow-md transition-all">
                <span className="text-2xl">{item.icon}</span>
                <span className="text-babyblue-700 font-medium">{item.title}</span>
                <ArrowRight className="w-4 h-4 text-babyblue-400 ml-auto" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EducationPage;
